import { Order, Pack, Product, Store } from "../../__generated__/graphql";
import { getOrderType } from "../../utils/order";
import ResponsiveCDNImage from "../common/image/responsive-cdn-image";
import MenuImageSlider from "./menu-image-slider";

/**
 * 주문 상품 목록 컴포넌트
 * 예약구매 팩인 경우 랜덤팩 표시
 * 실시간구매 팩인 경우 상품 목록 표시
 *
 * NOTE: Pack과 Menu는 실시간으로 변동되므로 접근하지 말 것
 *
 * @param order Order { Pack { Store } & Product } 포함 필수
 * @returns JSX.Element
 */
export default function OrderItems({
  order,
}: {
  order: Order & { pack?: Pack & { store?: Store } } & { products: Product[] };
}): JSX.Element {
  if (getOrderType(order) === "예약구매") {
    return (
      <div className="flex items-start bg-white rounded-lg border-b last:border-none py-2">
        <ResponsiveCDNImage
          src={order?.pack?.store?.img || ""}
          alt={order?.pack?.name || ""}
          className="w-[137px] h-[137px] object-cover rounded-lg"
          sizeRatio={1 / 2}
        />
        <div className="ml-3 flex flex-col flex-1">
          <p className="font-bold text-gray-900 mb-1">
            {order?.pack?.store?.title}랜덤팩
          </p>
          <p className="font-medium text-lg text-gray-500">
            <span className="font-semibold text-gray-900">
              {order?.baseAmount?.toLocaleString()}원
            </span>{" "}
            <span className="text-gray-400 text-lg line-through decoration-1">
              {(
                (order?.baseAmount || 0) + (order?.totalDiscount || 0)
              )?.toLocaleString()}
              원
            </span>
          </p>
          <p className="font-medium text-lg text-gray-500 mb-1">
            {order?.totalQuantity}개 구매
          </p>
        </div>
      </div>
    );
  } else if (getOrderType(order) === "실시간구매") {
    return (
      <>
        {order.products.map((product: Product) => {
          /**
           * '메뉴에서 선택하기'로 생성된 실시간 팩
           */
          if (product.menus && product.menus.length > 0) {
            return (
              <div
                key={product.id}
                className="flex items-start bg-white border-b last:border-none py-2"
              >
                <div className="w-[137px] h-[137px] relative rounded-lg overflow-hidden shrink-0">
                  <MenuImageSlider
                    productImage={product.img}
                    menus={product.menus}
                  />
                </div>
                <div className="ml-3 flex flex-col flex-1">
                  {/* 메뉴 이름 */}
                  <p className="font-medium text-gray-900 mb-1 break-keep">
                    {product?.name || ""}
                  </p>
                  {/* 메뉴 가격 */}
                  <p className="font-medium text-lg text-gray-500">
                    {product.userPrice?.toLocaleString()}원{" "}
                    <span className="text-gray-400 text-lg line-through decoration-1">
                      {product.price?.toLocaleString()}원
                    </span>
                  </p>
                  <p className="font-medium text-lg text-gray-500">
                    {product.menus.reduce(
                      (acc, menu) => acc + (menu?.quantity || 0),
                      0
                    )}
                    개 구성{" "}
                    {product.quantity > 1 && (
                      <span className="text-gray-400 text-lg font-semibold bg-gray-100 px-2 py-1 rounded-full">
                        x{product.quantity}팩
                      </span>
                    )}
                  </p>
                </div>
              </div>
            );
          } else {
            /**
             * '직접 올리기'로 생성된 실시간팩
             */
            return (
              <div
                key={product.id}
                className="flex items-start bg-white border-b last:border-none py-2"
              >
                <ResponsiveCDNImage
                  src={product.img || ""}
                  alt={product.name || ""}
                  className="w-[137px] h-[137px] object-cover rounded-lg"
                  sizeRatio={1 / 2}
                />

                <div className="ml-3 flex flex-col flex-1">
                  {/* 직접 올리기 팩 이름 */}
                  <p className="font-medium text-gray-900">
                    {product.name || ""}
                  </p>
                  {/* 직접 올리기 팩 가격 */}
                  <p className="font-medium text-lg text-gray-500">
                    {product.userPrice?.toLocaleString()}원{" "}
                    <span className="text-gray-400 text-lg line-through decoration-1">
                      {product.price?.toLocaleString()}
                    </span>
                  </p>
                  {/* NOTE: 직접 올리기 팩 수량은 1개로 고정 */}
                  <p className="font-medium text-lg text-gray-500">1개 구매</p>
                </div>
              </div>
            );
          }
        })}
      </>
    );
  }

  return <></>;
}
